import { defineComponent as _defineComponent } from 'vue'
import { HatchStyle } from 'farmdok-rest-api';
import { onMounted, onUnmounted, ref } from 'vue';

// JS Fiddle example used for this component: https://jsfiddle.net/o4phfL6c/
// This component is used to convert a google.maps.Polygon path to a SVG overlay, because google maps polygon has no ability to draw a hatch pattern.
// based on the HatchType a different pattern/rect is drawn


export default /*#__PURE__*/_defineComponent({
  __name: 'HatchFieldOverlay',
  props: {
    map: null,
    path: null,
    primaryColor: null,
    secondaryColor: null,
    hatch: null
  },
  setup(__props: any) {

const props = __props as {
  map: google.maps.Map | null;
  path: google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>>;
  primaryColor?: string;
  secondaryColor?: string;
  hatch?: HatchStyle;
}



const bounds = ref<google.maps.LatLngBounds>();
const overlayPath = ref<SVGPathElement>();
const overlayDiv = ref<HTMLDivElement | null>();
const overlayView = ref<google.maps.OverlayView | null>();
const polygonPath = ref();

/* LIFECYCLE */

onMounted(() => {
  init();
});

onUnmounted(() => {
  overlayView.value?.setMap(null);
});

/* METHODS */

function getPatternRotation(hatch: HatchStyle) {
  switch (hatch) {
    case HatchStyle.ForwardDiagonal:
      return 45;
    case HatchStyle.BackwardDiagonal:
      return 135;
    case HatchStyle.Horizontal:
      return 0;
    case HatchStyle.Vertical:
      return 90;
    case HatchStyle.CheckerBoard:
      return -45;
    case HatchStyle.Grid:
      return 0;
    default:
      return 0;
  }
}

function getPatternSize() {
  if (props.hatch === HatchStyle.Grid) {
    return '4';
  }
  return '3.5';
}

function getPattern(hatch: HatchStyle) {
  const uniquePatternId = `lineFill_${Math.random().toString(36).substr(2, 9)}`;
  const svgns = 'http://www.w3.org/2000/svg';
  const pattern = document.createElementNS(svgns, 'pattern');
  pattern.setAttributeNS(null, 'id', uniquePatternId);
  pattern.setAttributeNS(null, 'patternUnits', 'userSpaceOnUse');
  pattern.setAttributeNS(null, 'patternTransform', `rotate(${getPatternRotation(hatch)})`);
  pattern.setAttributeNS(null, 'height', getPatternSize());
  pattern.setAttributeNS(null, 'width', getPatternSize());

  const uniqueRectId = `rectFill_${Math.random().toString(36).substr(2, 9)}`;

  if (
    hatch === HatchStyle.Horizontal ||
    hatch === HatchStyle.Vertical ||
    hatch === HatchStyle.ForwardDiagonal ||
    hatch === HatchStyle.BackwardDiagonal
  ) {
    const rect = document.createElementNS(svgns, 'rect');
    rect.setAttributeNS(null, 'id', uniqueRectId);
    rect.setAttributeNS(null, 'fill', props.primaryColor ?? '#000');
    rect.setAttributeNS(null, 'fill-opacity', '1');
    rect.setAttributeNS(null, 'stroke', props.secondaryColor ?? 'grey');
    rect.setAttributeNS(null, 'stroke-width', '2');
    rect.setAttributeNS(null, 'stroke-dasharray', '3.5,3.5');
    rect.setAttributeNS(null, 'height', '3.5');
    rect.setAttributeNS(null, 'width', '3.5');
    pattern.appendChild(rect);
  }

  if (hatch === HatchStyle.Grid) {
    const rect = document.createElementNS(svgns, 'rect');
    rect.setAttributeNS(null, 'id', uniqueRectId);
    rect.setAttributeNS(null, 'fill', props.primaryColor ?? '#000');
    rect.setAttributeNS(null, 'height', '7');
    rect.setAttributeNS(null, 'width', '7');
    pattern.appendChild(rect);

    const horizontalLine = document.createElementNS(svgns, 'line');
    horizontalLine.setAttributeNS(null, 'x1', '0');
    horizontalLine.setAttributeNS(null, 'y1', '3.5');
    horizontalLine.setAttributeNS(null, 'x2', '7');
    horizontalLine.setAttributeNS(null, 'y2', '3.5');
    horizontalLine.setAttributeNS(null, 'stroke', props.secondaryColor ?? 'grey');
    horizontalLine.setAttributeNS(null, 'stroke-width', '1');
    pattern.appendChild(horizontalLine);

    const verticalLine = document.createElementNS(svgns, 'line');
    verticalLine.setAttributeNS(null, 'x1', '3.5');
    verticalLine.setAttributeNS(null, 'y1', '0');
    verticalLine.setAttributeNS(null, 'x2', '3.5');
    verticalLine.setAttributeNS(null, 'y2', '7');
    verticalLine.setAttributeNS(null, 'stroke', props.secondaryColor ?? 'grey');
    verticalLine.setAttributeNS(null, 'stroke-width', '1');
    pattern.appendChild(verticalLine);
  }

  if (hatch === HatchStyle.CheckerBoard) {
    return getCheckerboardPattern();
  }

  if (hatch === HatchStyle.Solid) {
    const rect = document.createElementNS(svgns, 'rect');
    rect.setAttributeNS(null, 'id', uniqueRectId);
    rect.setAttributeNS(null, 'fill', props.primaryColor ?? '#000');
    rect.setAttributeNS(null, 'height', '7');
    rect.setAttributeNS(null, 'width', '7');
    pattern.appendChild(rect);
  }

  return pattern;
}

function getCheckerboardPattern() {
  const uniquePatternId = `lineFill_${Math.random().toString(36).substr(2, 9)}`;
  const svgns = 'http://www.w3.org/2000/svg';
  const pattern = document.createElementNS(svgns, 'pattern');
  pattern.setAttributeNS(null, 'id', uniquePatternId);
  pattern.setAttributeNS(null, 'patternUnits', 'userSpaceOnUse');
  pattern.setAttributeNS(null, 'height', '6');
  pattern.setAttributeNS(null, 'width', '6');

  const rect1 = document.createElementNS(svgns, 'rect');
  rect1.setAttributeNS(null, 'fill', props.secondaryColor ?? '#000');
  rect1.setAttributeNS(null, 'height', '3');
  rect1.setAttributeNS(null, 'width', '3');
  rect1.setAttributeNS(null, 'x', '0');
  rect1.setAttributeNS(null, 'y', '0');
  pattern.appendChild(rect1);

  const rect2 = document.createElementNS(svgns, 'rect');
  rect2.setAttributeNS(null, 'fill', props.primaryColor ?? '#000');
  rect2.setAttributeNS(null, 'height', '3');
  rect2.setAttributeNS(null, 'width', '3');
  rect2.setAttributeNS(null, 'x', '3');
  rect2.setAttributeNS(null, 'y', '0');
  pattern.appendChild(rect2);

  const rect3 = document.createElementNS(svgns, 'rect');
  rect3.setAttributeNS(null, 'fill', props.secondaryColor ?? '#000');
  rect3.setAttributeNS(null, 'height', '3');
  rect3.setAttributeNS(null, 'width', '3');
  rect3.setAttributeNS(null, 'x', '3');
  rect3.setAttributeNS(null, 'y', '3');
  pattern.appendChild(rect3);

  const rect4 = document.createElementNS(svgns, 'rect');
  rect4.setAttributeNS(null, 'fill', props.primaryColor ?? '#000');
  rect4.setAttributeNS(null, 'height', '3');
  rect4.setAttributeNS(null, 'width', '3');
  rect4.setAttributeNS(null, 'x', '0');
  rect4.setAttributeNS(null, 'y', '3');
  pattern.appendChild(rect4);

  return pattern;
}

function init() {
  bounds.value = new google.maps.LatLngBounds();
  polygonPath.value = convertMVCArrayToPath(props.path);

  polygonPath.value.forEach((path: google.maps.MVCArray) => {
    path.forEach((point: google.maps.LatLng) => {
      bounds.value?.extend(point);
    });
  });

  overlayView.value = new google.maps.OverlayView();

  overlayView.value.setMap(props.map);

  overlayView.value.onRemove = () => {
    const panes = overlayView.value?.getPanes();
    panes?.overlayLayer.removeChild(overlayDiv.value!);
    overlayDiv.value?.parentNode?.removeChild(overlayDiv.value);
    overlayDiv.value = null;
    overlayView.value = null;
  };

  overlayView.value.onAdd = () => {
    if (!props.hatch) {
      return;
    }
    const div = document.createElement('div');
    div.style.borderStyle = 'none';
    div.style.borderWidth = '0px';
    div.style.position = 'absolute';

    const svgns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgns, 'svg');
    svg.setAttributeNS(null, 'preserveAspectRatio', 'xMidYMid meet');

    const def = document.createElementNS(svgns, 'defs');

    const pattern = getPattern(props.hatch);
    def.appendChild(pattern);
    svg.appendChild(def);

    const path = document.createElementNS(svgns, 'path');
    path.setAttributeNS(null, 'fill', `url(#${pattern.id})`);
    overlayPath.value = path;
    svg.appendChild(path);

    div.appendChild(svg);
    div.classList.add('polygon');

    overlayDiv.value = div;

    const panes = overlayView.value?.getPanes();
    panes?.overlayLayer.appendChild(overlayDiv.value!);
  };

  overlayView.value.draw = () => {
    const overlayProjection = overlayView.value?.getProjection();

    if (!bounds.value || !overlayDiv.value) {
      return;
    }

    if (bounds.value) {
      const sw = overlayProjection?.fromLatLngToDivPixel(bounds.value.getSouthWest());
      const ne = overlayProjection?.fromLatLngToDivPixel(bounds.value.getNorthEast());

      const div = overlayDiv.value;
      div.style.left = `${sw!.x}px`;
      div.style.top = `${ne!.y}px`;
      div.style.width = `${ne!.x - sw!.x}px`;
      div.style.height = `${sw!.y - ne!.y}px`;
    }
    overlayPath.value?.setAttributeNS(null, 'd', mapPointstoSVGPath());
  };
}

/**
 * Converts google.maps.LatLng to SVG path
 */
function mapPointstoSVGPath(): string {
  const proj = overlayView.value?.getProjection();

  if (!bounds.value) {
    return '';
  }

  const sw = proj?.fromLatLngToDivPixel(bounds.value?.getSouthWest());
  const ne = proj?.fromLatLngToDivPixel(bounds.value?.getNorthEast());

  let adjustedCoords = '';
  for (let i = 0; i < polygonPath.value.length; i += 1) {
    let points = '';
    for (let j = 0; j < polygonPath.value[i].length; j += 1) {
      const point = proj?.fromLatLngToDivPixel(polygonPath.value[i][j]);
      if (point && sw && ne) {
        if (j === 0) {
          points += `${point.x - sw.x}, ${point.y - ne.y}`;
        } else {
          points += ` ${point.x - sw.x}, ${point.y - ne.y}`;
        }
      }
    }
    adjustedCoords += `M${points}z `;
  }
  return adjustedCoords;
}

function convertMVCArrayToPath(
  mvcArray: google.maps.MVCArray<google.maps.MVCArray<google.maps.LatLng>>,
): google.maps.LatLng[][] {
  const path: google.maps.LatLng[][] = [];

  mvcArray.forEach((coordinates) => {
    const polygon: google.maps.LatLng[] = [];

    coordinates.forEach((latlng) => {
      polygon.push(latlng);
    });

    path.push(polygon);
  });

  return path;
}

return { __sfc: true,props, bounds, overlayPath, overlayDiv, overlayView, polygonPath, getPatternRotation, getPatternSize, getPattern, getCheckerboardPattern, init, mapPointstoSVGPath, convertMVCArrayToPath }
}

})